"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
  function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
    function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
    function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformJsonLdDocumentToReactAdminDocument = void 0;
const react_admin_1 = require("react-admin");
const lodash_isplainobject_1 = __importDefault(require("lodash.isplainobject"));
const api_doc_parser_1 = require("@api-platform/api-doc-parser");
const fetchHydra_1 = __importDefault(require("./fetchHydra"));
const schemaAnalyzer_1 = require("./schemaAnalyzer");
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPlainObject = (value) => (0, lodash_isplainobject_1.default)(value);
class ReactAdminDocument {
  constructor(obj) {
    Object.assign(this, obj);
    if (obj.id) {
      this.originId = obj.id.toString();
    }
    if (!obj['@id']) {
      throw new Error('Document needs to have an @id member.');
    }
    this.id = obj['@id'];
  }
  toString() {
    return `[object ${this.id}]`;
  }
}
/**
 * Local cache containing embedded documents.
 * It will be used to prevent useless extra HTTP query if the relation is displayed.
 */
const reactAdminDocumentsCache = new Map();
/**
 * Transforms a JSON-LD document to a react-admin compatible document.
 */
const transformJsonLdDocumentToReactAdminDocument = (jsonLdDocument, clone = true, addToCache = true, useEmbedded = false) => {
  let clonedDocument = jsonLdDocument;
  if (clone) {
    // deep clone documents
    clonedDocument = JSON.parse(JSON.stringify(clonedDocument));
  }
  const document = new ReactAdminDocument(clonedDocument);
  // Replace embedded objects by their IRIs, and store the object itself in the cache to reuse without issuing new HTTP requests.
  Object.keys(document).forEach((key) => {
    // to-one
    if (isPlainObject(document[key]) && document[key]['@id']) {
      if (addToCache) {
        reactAdminDocumentsCache.set(document[key]['@id'], (0, exports.transformJsonLdDocumentToReactAdminDocument)(document[key], false, false));
      }
      document[key] = useEmbedded ? document[key] : document[key]['@id'];
      return;
    }
    // to-many
    if (Array.isArray(document[key]) &&
      document[key].length &&
      isPlainObject(document[key][0]) &&
      document[key][0]['@id']) {
      document[key] = document[key].map((obj) => {
        if (addToCache && obj['@id']) {
          reactAdminDocumentsCache.set(obj['@id'], (0, exports.transformJsonLdDocumentToReactAdminDocument)(obj, false, false));
        }
        return useEmbedded ? obj : obj['@id'];
      });
    }
  });
  return document;
};
exports.transformJsonLdDocumentToReactAdminDocument = transformJsonLdDocumentToReactAdminDocument;
const extractHubUrl = (response) => {
  const linkHeader = response.headers.get('Link');
  if (!linkHeader) {
    return null;
  }
  const matches = linkHeader.match(/<([^>]+)>;\s+rel=(?:mercure|"[^"]*mercure[^"]*")/);
  return (matches === null || matches === void 0 ? void 0 : matches[1]) ? matches[1] : null;
};
const createSubscription = (mercure, topic, callback) => {
  if (mercure.hub === null) {
    return {
      subscribed: false,
      topic,
      callback,
      count: 1,
    };
  }
  const url = new URL(mercure.hub, window.origin);
  url.searchParams.append('topic', new URL(topic, mercure.topicUrl).toString());
  if (mercure.jwt !== null) {
    document.cookie = `mercureAuthorization=${mercure.jwt}; Path=${mercure.hub}; Secure; SameSite=None`;
  }
  const eventSource = new EventSource(url.toString(), {
    withCredentials: mercure.jwt !== null,
  });
  const eventListener = (event) => {
    const document = (0, exports.transformJsonLdDocumentToReactAdminDocument)(JSON.parse(event.data));
    // the only need for this callback is for accessing redux's `dispatch` method to update RA's state.
    callback(document);
  };
  eventSource.addEventListener('message', eventListener);
  return {
    subscribed: true,
    topic,
    callback,
    eventSource,
    eventListener,
    count: 1,
  };
};
const defaultParams = {
  httpClient: fetchHydra_1.default,
  apiDocumentationParser: api_doc_parser_1.parseHydraDocumentation,
  mercure: {},
  useEmbedded: false,
  disableCache: false,
};
/**
 * Maps react-admin queries to a Hydra powered REST API
 *
 * @see http://www.hydra-cg.com/
 *
 * @example
 * CREATE   => POST http://my.api.url/posts/123
 * DELETE   => DELETE http://my.api.url/posts/123
 * GET_LIST => GET http://my.api.url/posts
 * GET_MANY => GET http://my.api.url/posts/123, GET http://my.api.url/posts/456, GET http://my.api.url/posts/789
 * GET_ONE  => GET http://my.api.url/posts/123
 * UPDATE   => PUT http://my.api.url/posts/123
 */
function dataProvider(entrypointOrParams, httpClient = fetchHydra_1.default, apiDocumentationParser = api_doc_parser_1.parseHydraDocumentation, useEmbedded = false) {
  let entrypoint;
  let mercure;
  let disableCache;
  if (typeof entrypointOrParams === 'string') {
    entrypoint = entrypointOrParams;
    mercure = {
      hub: null,
      jwt: null,
      topicUrl: entrypoint,
    };
    disableCache = false;
  }
  if (typeof entrypointOrParams === 'object') {
    const params = Object.assign(Object.assign({}, defaultParams), entrypointOrParams);
    entrypoint = params.entrypoint;
    // eslint-disable-next-line no-param-reassign
    httpClient = params.httpClient;
    // eslint-disable-next-line no-param-reassign
    apiDocumentationParser = params.apiDocumentationParser;
    mercure = Object.assign({ hub: null, jwt: null, topicUrl: params.entrypoint }, params.mercure);
    disableCache = params.disableCache;
    // eslint-disable-next-line no-param-reassign
    useEmbedded = params.useEmbedded;
  }
  else {
    // eslint-disable-next-line no-console
    console.warn('Passing a list of arguments for building the data provider is deprecated. Please use an object instead.');
  }
  let apiSchema;
  // store mercure subscriptions
  const subscriptions = {};
  const convertReactAdminDataToHydraData = (resource, data = {}) => {
    const reactAdminData = data;
    const fieldData = {};
    if (resource.fields) {
      resource.fields.forEach(({ name, reference, normalizeData }) => {
        if (!(name in reactAdminData)) {
          return;
        }
        if (reference && reactAdminData[name] === '') {
          reactAdminData[name] = null;
          return;
        }
        if (undefined === normalizeData) {
          return;
        }
        fieldData[name] = normalizeData(reactAdminData[name]);
      });
    }
    const fieldDataKeys = Object.keys(fieldData);
    const fieldDataValues = Object.values(fieldData);
    return Promise.all(fieldDataValues).then((normalizedFieldData) => {
      const object = {};
      for (let i = 0; i < fieldDataKeys.length; i += 1) {
        const key = fieldDataKeys[i];
        if (key) {
          object[key] = normalizedFieldData[i];
        }
      }
      return Object.assign(Object.assign({}, reactAdminData), object);
    });
  };
  const transformReactAdminDataToRequestBody = (resource, data, extraInformation) => {
    const apiResource = apiSchema.resources.find(({ name }) => resource === name);
    if (undefined === apiResource) {
      return Promise.resolve(data);
    }
    return convertReactAdminDataToHydraData(apiResource, data).then((hydraData) => {
      const values = Object.values(hydraData);
      const containFile = (element) => isPlainObject(element) &&
        Object.values(element).some((value) => value instanceof File);
      const hasToJSON = (element) => !!element &&
        typeof element !== 'string' &&
        typeof element.toJSON === 'function';
      if (!extraInformation.hasFileField &&
        !values.some((value) => containFile(value))) {
        return JSON.stringify(hydraData);
      }
      const body = new FormData();
      Object.entries(hydraData).forEach(([key, value]) => {
        // React-Admin FileInput format is an object containing a file.
        if (containFile(value)) {
          body.append(key, Object.values(value).find((val) => val instanceof File));
          return;
        }
        if (hasToJSON(value)) {
          body.append(key, value.toJSON());
          return;
        }
        if (isPlainObject(value) || Array.isArray(value)) {
          body.append(key, JSON.stringify(value));
          return;
        }
        body.append(key, value);
      });
      return body;
    });
  };
  const shouldUseItemUrl = (type) => {
    switch (type) {
      case 'GET_ONE':
      case 'UPDATE':
      case 'DELETE':
        return true;
      default:
        return false;
    }
  };
  const convertReactAdminRequestToHydraRequest = (type, resource, dataProviderParams) => {
    var _a;
    const params = dataProviderParams;
    const entrypointUrl = new URL(entrypoint, window.location.href);
    let url;
    if ('id' in params && shouldUseItemUrl(type)) {
      url = new URL(params.id.toString(), entrypointUrl);
    }
    else {
      url = new URL(`${entrypoint}/${resource}`, entrypointUrl);
    }
    const searchParams = (_a = params.searchParams) !== null && _a !== void 0 ? _a : {};
    const searchParamKeys = Object.keys(searchParams);
    searchParamKeys.forEach((searchParamKey) => {
      const searchParam = searchParams[searchParamKey];
      if (searchParam) {
        url.searchParams.set(searchParamKey, searchParam);
      }
    });
    let extraInformation = {};
    if ('data' in params && params.data.extraInformation) {
      extraInformation = params.data.extraInformation;
      delete params.data.extraInformation;
    }
    const updateHttpMethod = extraInformation.hasFileField ? 'POST' : 'PATCH';
    switch (type) {
      case react_admin_1.CREATE:
        return transformReactAdminDataToRequestBody(resource, params.data, extraInformation).then((body) => ({
          options: {
            body,
            method: 'POST',
          },
          url,
        }));
      case react_admin_1.DELETE:
        return Promise.resolve({
          options: {
            method: 'DELETE',
          },
          url,
        });
      case react_admin_1.GET_LIST:
      case react_admin_1.GET_MANY_REFERENCE: {
        const { pagination: { page, perPage }, sort: { field, order }, filter, } = params;
        if (order && field) {
          url.searchParams.set(`order[${field}]`, order);
        }
        if (page)
          url.searchParams.set('page', page.toString());
        if (perPage)
          url.searchParams.set('itemsPerPage', perPage.toString());
        if (filter) {
          const buildFilterParams = (key, nestedFilter, rootKey) => {
            const filterValue = nestedFilter[key];
            if (filterValue === undefined) {
              return;
            }
            if (Array.isArray(filterValue)) {
              filterValue.forEach((arrayFilterValue, index) => {
                url.searchParams.set(`${rootKey}[${index}]`, arrayFilterValue);
              });
              return;
            }
            if (!isPlainObject(filterValue)) {
              url.searchParams.set(rootKey, filterValue.toString());
              return;
            }
            Object.keys(filterValue).forEach((subKey) => {
              if (rootKey === 'exists' ||
                [
                  'after',
                  'before',
                  'strictly_after',
                  'strictly_before',
                  'lt',
                  'gt',
                  'lte',
                  'gte',
                  'between',
                ].includes(subKey)) {
                buildFilterParams(subKey, filterValue, `${rootKey}[${subKey}]`);
                return;
              }
              buildFilterParams(subKey, filterValue, `${rootKey}.${subKey}`);
            });
          };
          Object.keys(filter).forEach((key) => {
            buildFilterParams(key, filter, key);
          });
        }
        if (type === react_admin_1.GET_MANY_REFERENCE) {
          const { target, id } = params;
          if (target) {
            url.searchParams.set(target, id.toString());
          }
        }

        if (resource === 'dictionary/products') {
          url.searchParams.append('t[]', 'product:company');
          url.searchParams.append('t[]', 'company');
        }

        return Promise.resolve({
          options: {},
          url,
        });
      }
      case react_admin_1.GET_ONE:
        return Promise.resolve({
          options: {},
          url,
        });
      case react_admin_1.UPDATE:
        delete params.data.deleted
        delete params.data.deletedAt
        delete params.data.originId

        if (resource === 'dictionary/companies') {
          delete params.data.products
        }

        return transformReactAdminDataToRequestBody(resource, params.data, extraInformation).then((body) => ({
          options: {
            body,
            method: updateHttpMethod,
          },
          url,
        }));
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
  };
  const convertHydraDataToReactAdminData = (resource, data) => {
    const apiResource = apiSchema.resources.find(({ name }) => resource === name);
    if (undefined === apiResource) {
      return Promise.resolve(data);
    }
    const fieldData = {};
    if (apiResource.fields) {
      apiResource.fields.forEach(({ name, denormalizeData }) => {
        if (!(name in data) || undefined === denormalizeData) {
          return;
        }
        fieldData[name] = denormalizeData(data[name]);
      });
    }
    const fieldDataKeys = Object.keys(fieldData);
    const fieldDataValues = Object.values(fieldData);
    return Promise.all(fieldDataValues).then((normalizedFieldData) => {
      const object = {};
      for (let i = 0; i < fieldDataKeys.length; i += 1) {
        const key = fieldDataKeys[i];
        if (key) {
          object[key] = normalizedFieldData[i];
        }
      }
      return Object.assign(Object.assign({}, data), object);
    });
  };
  const convertHydraResponseToReactAdminResponse = (type, resource, params, response) => {
    if (mercure.hub === null) {
      const hubUrl = extractHubUrl(response);
      if (hubUrl) {
        mercure.hub = hubUrl;
        const subKeys = Object.keys(subscriptions);
        subKeys.forEach((subKey) => {
          const sub = subscriptions[subKey];
          if (sub && !sub.subscribed) {
            subscriptions[subKey] = createSubscription(mercure, sub.topic, sub.callback);
          }
        });
      }
    }
    switch (type) {
      case react_admin_1.GET_LIST:
      case react_admin_1.GET_MANY_REFERENCE:
        if (!response.json) {
          return Promise.reject(new Error(`An empty response was received for "${type}".`));
        }
        if (!('hydra:member' in response.json)) {
          return Promise.reject(new Error(`Response doesn't have a "hydra:member" field.`));
        }
        // TODO: support other prefixes than "hydra:"
        // eslint-disable-next-line no-case-declarations
        const hydraCollection = response.json;
        return Promise.resolve(hydraCollection['hydra:member'].map((document) => (0, exports.transformJsonLdDocumentToReactAdminDocument)(document, true, !disableCache, useEmbedded)))
        .then((data) => Promise.all(data.map((hydraData) => convertHydraDataToReactAdminData(resource, hydraData))))
        .then((data) => {
          let total = -3; // no information
          if (hydraCollection['hydra:view']) {
            total = hydraCollection['hydra:view']['hydra:next']
              ? -2 // there is a next page
              : -1; // no next page
          }
          if (hydraCollection['hydra:totalItems'] !== undefined) {
            total = hydraCollection['hydra:totalItems'];
          }
          return {
            data,
            total,
          };
        });
      case react_admin_1.DELETE:
        return Promise.resolve({ data: { id: params.id } });
      default:
        if (!response.json) {
          return Promise.reject(new Error(`An empty response was received for "${type}".`));
        }
        return Promise.resolve((0, exports.transformJsonLdDocumentToReactAdminDocument)(response.json, true, !disableCache, useEmbedded))
        .then((data) => convertHydraDataToReactAdminData(resource, data))
        .then((data) => ({ data }));
    }
  };
  const fetchApi = (type, resource, params) =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    convertReactAdminRequestToHydraRequest(type, resource, params)
    .then(({ url, options }) => httpClient(url, options))
    .then((response) => convertHydraResponseToReactAdminResponse(type, resource, params, response));
  /*
   * The fetchAllPages method allows running as many requests as needed in order to load all pages of a list.
   * This function uses the already transformed react-admin response data and could be improved by using the hydra pagination.
   */
  const fetchAllPages = (type, resource, params, previousResult) => __awaiter(this, void 0, void 0, function* () {
    const pageParams = params;
    const pageResult = (yield fetchApi(type, resource, pageParams));
    const result = previousResult !== null && previousResult !== void 0 ? previousResult : pageResult;
    if (previousResult) {
      result.data.push(...pageResult.data);
      if (pageResult.total < result.total) {
        // The total can have changed between 2 requests
        result.total = pageResult.total;
      }
    }
    // Minimalist infinite loop protection
    if (pageParams.pagination.page >= result.data.length) {
      return result;
    }
    if (pageResult.data.length > 0 && result.data.length < result.total) {
      pageParams.pagination.page += 1;
      return fetchAllPages(type, resource, pageParams, result);
    }
    return result;
  });
  const hasIdSearchFilter = (resource) => {
    const schema = apiSchema.resources.find((r) => r.name === resource);
    if (!schema) {
      return Promise.resolve(false);
    }
    return (0, schemaAnalyzer_1.resolveSchemaParameters)(schema).then((parameters) => parameters.map((filter) => filter.variable).includes('id'));
  };
  return {
    getList: (resource, params) => fetchApi(react_admin_1.GET_LIST, resource, params),
    getOne: (resource, params) => fetchApi(react_admin_1.GET_ONE, resource, params),
    getMany: (resource, params) =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      hasIdSearchFilter(resource).then((result) => {
        var _a;
        // Hydra doesn't handle MANY requests but if a search filter for the id is available, it is used.
        if (result) {
          return fetchAllPages(react_admin_1.GET_LIST, resource, {
            pagination: {
              // Asking for the good amount of items, as we could want to retrieve more items than the default amount sent by the API.
              perPage: (_a = params.ids) === null || _a === void 0 ? void 0 : _a.length,
              page: 1,
            },
            filter: { id: params.ids },
            sort: { field: '', order: '' },
          }).then(({ data }) => ({ data }));
        }
        // Else fallback to calling the ONE request n times instead.
        return Promise.all(params.ids.map((id) => {
          const document = reactAdminDocumentsCache.get(id.toString());
          if (document) {
            return Promise.resolve({ data: document });
          }
          return fetchApi(react_admin_1.GET_ONE, resource, { id });
        })).then((responses) => ({ data: responses.map(({ data }) => data) }));
      }),
    getManyReference: (resource, params) => fetchApi(react_admin_1.GET_MANY_REFERENCE, resource, params),
    update: (resource, params) => fetchApi(react_admin_1.UPDATE, resource, params),
    updateMany: (resource, params) => Promise.all(params.ids.map((id) => fetchApi(react_admin_1.UPDATE, resource, Object.assign(Object.assign({}, params), { id, previousData: { id } })))).then(() => ({ data: [] })),
    create: (resource, params) => fetchApi(react_admin_1.CREATE, resource, params),
    delete: (resource, params) => fetchApi(react_admin_1.DELETE, resource, params),
    deleteMany: (resource, params) => Promise.all(params.ids.map((id) => fetchApi(react_admin_1.DELETE, resource, { id }))).then(() => ({ data: [] })),
    introspect: (_resource = '', _params = {}) => apiSchema
      ? Promise.resolve({ data: apiSchema })
      : apiDocumentationParser(entrypoint)
      .then(({ api, customRoutes = [] }) => {
        if (api.resources && api.resources.length > 0) {
          apiSchema = Object.assign(Object.assign({}, api), { resources: api.resources });
        }
        return { data: api, customRoutes };
      })
      .catch((err) => {
        const { status, error } = err;
        let { message } = err;
        // Note that the `api-doc-parser` rejects with a non-standard error object hence the check
        if (error === null || error === void 0 ? void 0 : error.message) {
          message = error.message;
        }
        throw new Error(`Cannot fetch API documentation:\n${message
          ? `${message}\nHave you verified that CORS is correctly configured in your API?\n`
          : ''}${status ? `Status: ${status}` : ''}`);
      }),
    subscribe: (resourceIds, callback) => {
      resourceIds.forEach((resourceId) => {
        const sub = subscriptions[resourceId];
        if (sub !== undefined) {
          sub.count += 1;
          return;
        }
        subscriptions[resourceId] = createSubscription(mercure, resourceId, callback);
      });
      return Promise.resolve({ data: null });
    },
    unsubscribe: (_resource, resourceIds) => {
      resourceIds.forEach((resourceId) => {
        const sub = subscriptions[resourceId];
        if (sub === undefined) {
          return;
        }
        sub.count -= 1;
        if (sub.count <= 0) {
          if (sub.subscribed && sub.eventSource && sub.eventListener) {
            sub.eventSource.removeEventListener('message', sub.eventListener);
            sub.eventSource.close();
          }
          delete subscriptions[resourceId];
        }
      });
      return Promise.resolve({ data: null });
    },
  };
}
exports.default = dataProvider;
//# sourceMappingURL=dataProvider.js.map
